// MarketView — extracted from App.js. Hosts the durable market-signals
// board, executive read, what-moved cards, charts, and macro-filter.
//
// Functionality is unchanged from the previous in-App rendering — it now
// lives at the `/` route so the new `/client/:clientId` routes can co-exist.

import React, { useEffect, useMemo, useState } from "react";
import MarketTrackBoard from "./components/dashboard/MarketTrackBoard";
import MarketTrackDetailPanel from "./components/dashboard/MarketTrackDetailPanel";
import MacroThemeMap from "./components/dashboard/MacroThemeMap";
import TrackCharts from "./components/dashboard/TrackCharts";
import MarketStructureLenses from "./components/dashboard/MarketStructureLenses";
import ExecutiveMarketRead from "./components/dashboard/ExecutiveMarketRead";
import WhatMovedThisWeek from "./components/dashboard/WhatMovedThisWeek";
import useMarketTracks from "./hooks/useMarketTracks";
import { trackEvent } from "./lib/analytics";

const Kpi = ({ label, value, tooltip }) => (
    <div className="px-6 py-4 border-r border-[hsl(var(--line))] last:border-r-0 flex-1 min-w-[140px]" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`} title={tooltip || undefined}>
        <div className="text-[32px] leading-[1.05] font-semibold tracking-[-0.025em] num text-[hsl(var(--ink))]">{value}</div>
        <div className="text-xs text-[hsl(var(--mute))] mt-1.5">{label}</div>
    </div>
);

const TrackKpiStrip = ({ summary, tracks }) => {
    const s = summary || {};
    // Detect governed dataset and derive per-bucket counts from the full
    // track list. When governed metadata is present we swap the legacy
    // "Active this week" + "Early-stage / uncurated" pair for the new
    // governed-bucket cards (Top-level, Weekly movements).
    let topLevel = 0; let subPatterns = 0; let weekly = 0; let governed = 0;
    for (const t of tracks || []) {
        const g = t && t._governed;
        if (!g) continue;
        governed += 1;
        if (g.isTopLevel) topLevel += 1;
        else if (g.isSubPattern) subPatterns += 1;
        else if (g.isWeeklyDynamic) weekly += 1;
    }
    const hasGoverned = governed > 0;
    if (hasGoverned) {
        return (
            <section className="card-flat flex flex-wrap" data-testid="kpi-strip">
                <Kpi label="Market signals" value={s.total ?? (tracks?.length ?? 0)} tooltip="All governed market signals parsed for this dashboard" />
                <Kpi label="Top-level signals" value={topLevel} tooltip="Default-visible signals (MARKET_VIEW_TOP_LEVEL)" />
                <Kpi label="Core signals" value={s.coreTracks ?? 0} tooltip="Durable, structurally significant top-level signals" />
                <Kpi label="Watchlist signals" value={s.watchlistTracks ?? 0} tooltip="Top-level signals showing structural promise but not yet core" />
                <Kpi label="Sub-patterns" value={subPatterns} tooltip="Underlying evidence clusters folded under a parent signal" />
                <Kpi label="Weekly movements" value={weekly} tooltip="Current weekly dynamics linked to a parent signal" />
            </section>
        );
    }
    return (
        <section className="card-flat flex flex-wrap" data-testid="kpi-strip">
            <Kpi label="Market signals" value={s.total ?? 0} tooltip="Curated market signals parsed for this dashboard" />
            <Kpi label="Core signals" value={s.coreTracks ?? 0} tooltip="Durable, structurally significant market signals" />
            <Kpi label="Watchlist signals" value={s.watchlistTracks ?? 0} tooltip="Signals showing structural promise but not yet core" />
            <Kpi label="Active this week" value={s.active ?? 0} tooltip="Signals active in the latest indexed week" />
            <Kpi label="Sub-patterns" value={s.subPatterns ?? 0} tooltip="Underlying evidence clusters folded under a parent signal" />
            <Kpi label="Early-stage / uncurated" value={(s.exploratoryClusters ?? 0) + (s.uncurated ?? 0)} tooltip="Hidden by default. Use the toggle on the Market Signals board to inspect them." />
        </section>
    );
};

const MarketView = ({ onDataReady }) => {
    const [selectedTrackId, setSelectedTrackId] = useState(null);
    const [search, setSearch] = useState("");
    const [roleFilter, setRoleFilter] = useState("ALL");
    const [showExploratory, setShowExploratory] = useState(false);
    const [macroThemeFilter, setMacroThemeFilter] = useState(null);

    // Selecting `Sub-pattern` or `Weekly movement` in the role dropdown
    // implicitly widens the board to include supporting signals, so the
    // user never lands on an empty `Weekly movements · 0` state. The
    // reverse — toggling supporting signals OFF while a supporting role
    // filter is active — resets the role filter back to `ALL`.
    const handleRoleFilter = (next) => {
        if ((next === "SUB_PATTERN" || next === "WEEKLY_DYNAMIC") && !showExploratory) {
            setShowExploratory(true);
        }
        setRoleFilter(next);
    };
    const handleToggleExploratory = () => {
        setShowExploratory((v) => {
            const nextOn = !v;
            if (!nextOn && (roleFilter === "SUB_PATTERN" || roleFilter === "WEEKLY_DYNAMIC")) {
                setRoleFilter("ALL");
            }
            return nextOn;
        });
    };

    const data = useMarketTracks({ selectedTrackId, showExploratory });

    // Bubble metadata up so the Header (rendered above the Routes) can show counts.
    useEffect(() => {
        if (onDataReady) onDataReady(data);
    }, [data, onDataReady]);

    const filteredBoardTracks = useMemo(() => {
        if (!macroThemeFilter) return data.visibleBoardTracks;
        return data.visibleBoardTracks.filter((t) => t.MacroTheme === macroThemeFilter);
    }, [data.visibleBoardTracks, macroThemeFilter]);

    useEffect(() => {
        if (!data.tracks.length) return;
        // A child row (supporting pattern / weekly movement) is "valid"
        // under the macro filter when its PARENT signal is in the
        // visible parent set — otherwise clicking a child would be
        // immediately reset to the first parent (the bug we are
        // fixing). We resolve the parent via the same governed-first
        // chain used by the board itself.
        const sel = selectedTrackId ? data.tracks.find((t) => t.TrackID === selectedTrackId) : null;
        const selParentId = sel
            ? (sel?._governed?.parentTrackId || sel.ParentTrackID || sel.parentTrackId || null)
            : null;
        const selfInFiltered = selectedTrackId && filteredBoardTracks.find((t) => t.TrackID === selectedTrackId);
        const parentInFiltered = selParentId && filteredBoardTracks.find((t) => t.TrackID === selParentId);
        if (selfInFiltered || parentInFiltered) return;
        const inAll = selectedTrackId && data.tracks.find((t) => t.TrackID === selectedTrackId);
        if (macroThemeFilter && filteredBoardTracks.length) {
            setSelectedTrackId(filteredBoardTracks[0].TrackID);
            return;
        }
        if (inAll) return;
        const top = data.visibleBoardTracks[0] || data.tracks[0];
        setSelectedTrackId(top ? top.TrackID : null);
    }, [data.tracks, data.visibleBoardTracks, filteredBoardTracks, macroThemeFilter, selectedTrackId]);

    const allWeeks = useMemo(() => {
        const set = new Set();
        for (const list of Object.values(data.historyByTrackId || {})) {
            for (const h of list || []) {
                if (h.YearWeek) set.add(h.YearWeek);
            }
        }
        return [...set].sort();
    }, [data.historyByTrackId]);

    if (data.error) {
        return <div className="card-flat p-4 text-sm text-[hsl(var(--negative))]">Failed to load tracks: {data.error}</div>;
    }
    if (data.loading && !data.tracks.length) {
        return <div className="card-flat p-10 text-center text-[hsl(var(--mute))]">Loading market signals…</div>;
    }

    return (
        <>
            <ExecutiveMarketRead
                briefing={data.latestBriefing}
                referencedSignals={data.briefingReferences}
                onSelectTrack={setSelectedTrackId}
            />
            <TrackKpiStrip summary={data.curationSummary} tracks={data.tracks} />
            <WhatMovedThisWeek
                cards={data.movementCards}
                weekLabel={data.latestIndexedWeek?.yearWeek}
                onSelectTrack={setSelectedTrackId}
            />
            <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
                <div className="xl:col-span-3 space-y-6">
                    <MarketTrackBoard
                        tracks={filteredBoardTracks}
                        allTracks={data.tracks}
                        selectedId={selectedTrackId}
                        onSelect={(tid) => {
                            setSelectedTrackId(tid);
                            if (tid) trackEvent("market_track_inspect", { track_id: tid });
                        }}
                        totalWeeks={allWeeks.length}
                        showExploratory={showExploratory}
                        onToggleExploratory={handleToggleExploratory}
                        summary={data.curationSummary}
                        search={search}
                        onSearch={setSearch}
                        roleFilter={roleFilter}
                        onRoleFilter={handleRoleFilter}
                        macroThemeFilter={macroThemeFilter}
                        onClearMacroTheme={() => setMacroThemeFilter(null)}
                    />
                    <MacroThemeMap
                        tracks={data.tracks}
                        selectedMacroTheme={macroThemeFilter}
                        onSelectMacroTheme={setMacroThemeFilter}
                        onClearMacroTheme={() => setMacroThemeFilter(null)}
                    />
                </div>
                <div className="xl:col-span-2">
                    <MarketTrackDetailPanel
                        track={data.selectedTrack}
                        history={data.selectedTrackHistory}
                        totalWeeks={allWeeks.length}
                        subPatterns={data.subPatterns}
                        relatedTracks={data.relatedTracks}
                        allTracks={data.tracks}
                        onSelectTrack={setSelectedTrackId}
                        latestSignal={data.selectedTrack ? (data.latestSignalByTrackId.get(data.selectedTrack.TrackID) || null) : null}
                        briefingFeature={data.selectedTrack ? data.briefingReferences.find((r) => r.trackId === data.selectedTrack.TrackID) || null : null}
                    />
                </div>
            </div>
            <MarketStructureLenses tracks={data.tracks} />
            <TrackCharts
                tracks={data.tracks}
                historyByTrackId={data.historyByTrackId}
                allWeeks={allWeeks}
                summary={data.curationSummary}
                selectedTrack={data.selectedTrack}
                onSelectTrack={setSelectedTrackId}
            />
            <footer className="divider pt-6 pb-10 text-xs text-[hsl(var(--mute))] flex justify-between flex-wrap gap-3">
                <div>Signal Intelligence · durable market signals with weekly evidence underneath.</div>
                <div className="num">{data.trackCount} market signals · {data.historyCount} weekly observations · {allWeeks.length} indexed weeks · {data.briefingCount} briefings</div>
            </footer>
        </>
    );
};

export default MarketView;
